import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { learningService } from '../../../services/learning'
import type {
  AttemptSubmission,
  ContentFeedbackReason,
  LearnerAttempt,
  LearningTemplate,
  LessonActivityContract,
  TemplateAssignment,
} from '../../../types/learningContent'

// ── Safe JSON readers (mirror the tolerant parsing used elsewhere in the learning UI) ──

const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : value == null ? '' : String(value))

const asStringList = (value: unknown): string[] => {
  if (value == null) return []
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>
        const text = asString(obj.label ?? obj.text ?? obj.term ?? obj.question ?? obj.answer ?? obj.option)
        return text ? [text] : []
      }
      const text = asString(item)
      return text ? [text] : []
    })
  }
  const text = asString(value)
  return text ? [text] : []
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null

interface ChoiceQuestion {
  id: string
  question: string
  options: string[]
}

interface MatchPair {
  left: string
  right: string
}

// A template stores the full LessonActivity contract in `lessonActivity`; the learner-
// facing content lives under `.activity`. We never read `.answerKey` for rendering before
// completion; the server sanitizes hidden answers and remains the scoring authority.
const lessonOf = (template: LearningTemplate | null): LessonActivityContract => {
  if (!template) return {}
  const direct = asRecord(template.lessonActivity) as LessonActivityContract | null
  return direct ?? {}
}

const activityType = (template: LearningTemplate | null, lesson: LessonActivityContract): string =>
  asString(lesson.activityType) || asString(template?.activityType) || 'reflection'

const activityObject = (lesson: LessonActivityContract): Record<string, unknown> =>
  asRecord(lesson.activity) ?? {}

const multipleChoiceQuestions = (lesson: LessonActivityContract): ChoiceQuestion[] => {
  const raw = activityObject(lesson).questions
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item, index) => {
    const obj = asRecord(item)
    if (!obj) return []
    const question = asString(obj.question)
    const options = asStringList(obj.options)
    if (!question || options.length === 0) return []
    return [{
      id: asString(obj.id) || `q${index + 1}`,
      question,
      options,
    }]
  })
}

const passageText = (lesson: LessonActivityContract): string =>
  asString(activityObject(lesson).passage) || asString(activityObject(lesson).text)

const checklistItems = (lesson: LessonActivityContract): string[] =>
  asStringList(activityObject(lesson).items)

const orderingItems = (lesson: LessonActivityContract): string[] =>
  {
    const raw = activityObject(lesson).items ?? activityObject(lesson).steps ?? activityObject(lesson).options
    return Array.isArray(raw) ? rotateOptions(asStringList(raw)) : []
  }

const matchingPairs = (lesson: LessonActivityContract): MatchPair[] => {
  const raw = activityObject(lesson).pairs ?? activityObject(lesson).matches ?? activityObject(lesson).items
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item) => {
    const obj = asRecord(item)
    if (!obj) return []
    const left = asString(obj.left ?? obj.term ?? obj.prompt ?? obj.question)
    const right = asString(obj.right ?? obj.match ?? obj.answer ?? obj.definition)
    return left && right ? [{ left, right }] : []
  })
}

const pronunciationPhrases = (lesson: LessonActivityContract): string[] =>
  asStringList(activityObject(lesson).phrases ?? activityObject(lesson).practiceTargets ?? activityObject(lesson).items)

const rotateOptions = (items: string[]): string[] => {
  if (items.length <= 1) return items
  return [...items.slice(1), items[0]]
}

// ── Assignment card metadata ──

const formatDate = (value: string | null | undefined) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const assignedByLabel = (assignment: TemplateAssignment) =>
  assignment.assignedByType === 'ai' ? 'AI coach'
    : assignment.assignedByType === 'system' ? 'Punch'
      : 'Mentor'

const statusPillClass = (status: string) =>
  status === 'completed' ? 'status-pill status-pill--approved'
    : status === 'in_progress' ? 'status-pill status-pill--draft'
      : 'status-pill status-pill--muted'

const statusLabel = (assignment: TemplateAssignment) => {
  if (assignment.status === 'completed' || assignment.completionStatus === 'completed') return 'Completed'
  if (assignment.status === 'in_progress' || assignment.completionStatus === 'in_progress') return 'In progress'
  return 'Assigned'
}

const difficultyLabel = (value: string | null | undefined) => (value ? value.replace(/_/g, ' ') : null)

const apiErrorMessage = (error: unknown, fallback: string) => {
  const message = (error as any)?.response?.data?.message
  return typeof message === 'string' && message.trim() ? message : fallback
}

// Answers keyed by question id. The backend grades objective activities from the stored
// template and the UI renders the persisted attempt afterwards.
type Answers = Record<string, unknown>

const stringAnswer = (answers: Answers, key: string): string => {
  const value = answers[key]
  return typeof value === 'string' ? value : ''
}

const stringListAnswer = (answers: Answers, key: string): string[] => {
  const value = answers[key]
  return Array.isArray(value) ? value.map(asString) : []
}

const recordAnswer = (answers: Answers, key: string): Record<string, string> => {
  const value = asRecord(answers[key])
  if (!value) return {}
  return Object.fromEntries(Object.entries(value).map(([entryKey, entryValue]) => [entryKey, asString(entryValue)]))
}

interface AssignmentRunnerProps {
  assignment: TemplateAssignment
  onClose: () => void
  onCompleted: (assignmentId: string) => void
}

// Opens a single assignment: loads the template, renders its lessonActivity, collects
// answers, submits an attempt, then shows the backend-authoritative score + feedback.
const AssignmentRunner: React.FC<AssignmentRunnerProps> = ({ assignment, onClose, onCompleted }) => {
  const [template, setTemplate] = useState<LearningTemplate | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [answers, setAnswers] = useState<Answers>({})
  const [submitting, setSubmitting] = useState(false)
  const [attempt, setAttempt] = useState<LearnerAttempt | null>(null)
  const [rating, setRating] = useState('')
  const [reportReason, setReportReason] = useState('')
  const [reportDetails, setReportDetails] = useState('')
  const [feedbackSaved, setFeedbackSaved] = useState(false)
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
  const startedAt = useMemo(() => Date.now(), [])

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)
    learningService.getContentTemplate(assignment.templateId)
      .then((response) => { if (active) setTemplate(response.data.template) })
      .catch(() => { if (active) setError('We could not open this lesson. It may have been removed by your coach.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [assignment.templateId])

  const lesson = lessonOf(template)
  const type = activityType(template, lesson)
  const questions = useMemo(() => multipleChoiceQuestions(lesson), [lesson])
  const orderOptions = useMemo(() => orderingItems(lesson), [lesson])
  const pairs = useMemo(() => matchingPairs(lesson), [lesson])
  const matchOptions = useMemo(() => rotateOptions(pairs.map((pair) => pair.right)), [pairs])
  const phrases = useMemo(() => pronunciationPhrases(lesson), [lesson])
  const completed = attempt?.status === 'completed'

  const submit = useCallback(async () => {
    if (!template) return
    setSubmitting(true)
    setError(null)
    const payload: AttemptSubmission = {
      templateId: template.id,
      assignmentId: assignment.id,
      answers,
      completionTimeSeconds: Math.max(0, Math.round((Date.now() - startedAt) / 1000)),
      status: 'completed',
    }
    try {
      const response = await learningService.submitContentAttempt(payload)
      setAttempt(response.data.attempt)
      void onCompleted(assignment.id)
    } catch (err) {
      setError(apiErrorMessage(err, 'We could not submit your answers. Please retry.'))
    } finally {
      setSubmitting(false)
    }
  }, [template, answers, assignment.id, startedAt, onCompleted])

  const submitFeedback = useCallback(async () => {
    if (!attempt || !template || (!rating && !reportReason)) return
    setFeedbackSubmitting(true)
    setError(null)
    try {
      await learningService.submitContentFeedback({
        attemptId: attempt.id,
        templateId: template.id,
        rating: rating ? Number(rating) : null,
        reportReason: reportReason ? reportReason as ContentFeedbackReason : null,
        reportDetails: reportDetails.trim() || null,
      })
      setFeedbackSaved(true)
    } catch (err) {
      setError(apiErrorMessage(err, 'We could not save your content feedback. Please retry.'))
    } finally {
      setFeedbackSubmitting(false)
    }
  }, [attempt, template, rating, reportReason, reportDetails])

  if (loading) {
    return (
      <div className="card stack" data-testid="assigned-learning-runner-loading">
        <p className="text-muted">Loading your lesson…</p>
      </div>
    )
  }

  if (error && !template) {
    return (
      <div className="card stack" data-testid="assigned-learning-runner-error">
        <div className="admin-error-banner" role="alert">{error}</div>
        <div className="button-row"><button type="button" className="btn btn-secondary" onClick={onClose}>Back</button></div>
      </div>
    )
  }

  const scoreText = completed && attempt?.score != null && attempt?.maxScore != null
    ? `${attempt.score}/${attempt.maxScore}`
    : null

  return (
    <div className="card stack" data-testid="assigned-learning-runner">
      <div className="button-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="stack-sm">
          <h4 style={{ margin: 0 }}>{lesson.lessonTitle || template?.title || 'Assigned lesson'}</h4>
          <span className="text-muted">{[template?.skill, difficultyLabel(template?.difficulty), type.replace(/_/g, ' ')].filter(Boolean).join(' · ')}</span>
        </div>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
      </div>

      {assignment.personalizedNote && <p className="helper" data-testid="assigned-learning-note">“{assignment.personalizedNote}”</p>}
      {lesson.prompt && <p className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{lesson.prompt}</p>}
      {lesson.instructions && <p className="helper" style={{ whiteSpace: 'pre-wrap' }}>{lesson.instructions}</p>}

      {passageText(lesson) && (
        <div className="stack-sm">
          <strong>Passage</strong>
          <p className="card text-prewrap" style={{ whiteSpace: 'pre-wrap', margin: 0 }} data-testid="assigned-learning-passage">{passageText(lesson)}</p>
        </div>
      )}

      {/* Multiple choice — tappable options. Hidden answers are not part of this payload. */}
      {type === 'multiple_choice' && questions.length > 0 && (
        <div className="stack" data-testid="assigned-learning-mc">
          {questions.map((question) => {
            const chosen = answers[question.id]
            return (
              <div key={question.id} className="stack-sm" data-testid="assigned-learning-question">
                <strong>{question.question}</strong>
                <div className="stack-sm">
                  {question.options.map((option) => {
                    const selected = chosen === option
                    const className = selected ? 'btn btn-primary'
                        : 'btn btn-secondary'
                    return (
                      <button
                        key={option}
                        type="button"
                        className={className}
                        disabled={completed || submitting}
                        aria-pressed={selected}
                        onClick={() => setAnswers((prev) => ({ ...prev, [question.id]: option }))}
                      >
                        {option}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Checklist — tappable items the learner confirms. */}
      {type === 'checklist' && checklistItems(lesson).length > 0 && (
        <div className="stack-sm" data-testid="assigned-learning-checklist">
          {checklistItems(lesson).map((item, index) => {
            const id = `item-${index}`
            const checked = stringAnswer(answers, id) === 'done'
            return (
              <button
                key={id}
                type="button"
                className={checked ? 'btn btn-primary' : 'btn btn-secondary'}
                disabled={completed || submitting}
                aria-pressed={checked}
                onClick={() => setAnswers((prev) => ({ ...prev, [id]: checked ? '' : 'done' }))}
              >
                {checked ? '☑ ' : '☐ '}{item}
              </button>
            )
          })}
        </div>
      )}

      {type === 'ordering' && orderOptions.length > 0 && (
        <div className="stack" data-testid="assigned-learning-ordering">
          <strong>Put the steps in order</strong>
          {orderOptions.map((_, index) => {
            const chosenOrder = stringListAnswer(answers, 'ordering')
            const value = chosenOrder[index] || ''
            return (
              <label key={`order-position-${index}`} className="stack-sm">
                <span className="helper">Step {index + 1}</span>
                <select
                  className="input"
                  value={value}
                  disabled={completed || submitting}
                  onChange={(event) => {
                    const next = [...chosenOrder]
                    next[index] = event.target.value
                    setAnswers((prev) => ({ ...prev, ordering: next }))
                  }}
                >
                  <option value="">Choose a step</option>
                  {orderOptions.map((item) => (
                    <option key={`${index}-${item}`} value={item}>{item}</option>
                  ))}
                </select>
              </label>
            )
          })}
        </div>
      )}

      {type === 'matching' && pairs.length > 0 && (
        <div className="stack" data-testid="assigned-learning-matching">
          <strong>Match each item</strong>
          {pairs.map((pair, index) => {
            const matches = recordAnswer(answers, 'matching')
            return (
              <label key={`${pair.left}-${index}`} className="stack-sm">
                <span>{pair.left}</span>
                <select
                  className="input"
                  value={matches[pair.left] || ''}
                  disabled={completed || submitting}
                  onChange={(event) => {
                    const next = { ...matches, [pair.left]: event.target.value }
                    setAnswers((prev) => ({ ...prev, matching: next }))
                  }}
                >
                  <option value="">Choose a match</option>
                  {matchOptions.map((option) => (
                    <option key={`${pair.left}-${option}`} value={option}>{option}</option>
                  ))}
                </select>
              </label>
            )
          })}
        </div>
      )}

      {type === 'pronunciation_prompt' && phrases.length > 0 && (
        <div className="stack" data-testid="assigned-learning-pronunciation">
          <strong>Practice each phrase</strong>
          {phrases.map((phrase, index) => {
            const id = `phrase-${index}`
            const practiced = stringAnswer(answers, id) === 'practiced'
            return (
              <button
                key={id}
                type="button"
                className={practiced ? 'btn btn-primary' : 'btn btn-secondary'}
                disabled={completed || submitting}
                aria-pressed={practiced}
                onClick={() => setAnswers((prev) => ({ ...prev, [id]: practiced ? '' : 'practiced' }))}
              >
                {practiced ? 'Practiced: ' : 'Practice: '}{phrase}
              </button>
            )
          })}
          <label className="stack-sm">
            <span className="helper">How did it feel?</span>
            <select
              className="input"
              value={stringAnswer(answers, 'pronunciationConfidence')}
              disabled={completed || submitting}
              onChange={(event) => setAnswers((prev) => ({ ...prev, pronunciationConfidence: event.target.value }))}
            >
              <option value="">Choose confidence</option>
              <option value="needs_practice">Needs practice</option>
              <option value="getting_clearer">Getting clearer</option>
              <option value="confident">Confident</option>
            </select>
          </label>
        </div>
      )}

      {/* Fallback for short_answer / reflection / unrecognised or sparse structured
          activity types: a labelled free-text response so the learner can always complete
          the assignment safely. */}
      {((type !== 'multiple_choice' || questions.length === 0)
        && type !== 'checklist'
        && (type !== 'ordering' || orderOptions.length === 0)
        && (type !== 'matching' || pairs.length === 0)
        && (type !== 'pronunciation_prompt' || phrases.length === 0)) && (
        <label className="stack-sm" data-testid="assigned-learning-freeform">
          <strong>Your response</strong>
          <textarea
            className="input"
            rows={5}
            value={stringAnswer(answers, 'response')}
            disabled={completed || submitting}
            onChange={(event) => setAnswers((prev) => ({ ...prev, response: event.target.value }))}
            placeholder="Write your answer here…"
          />
        </label>
      )}

      {error && <div className="admin-error-banner" role="alert" data-testid="assigned-learning-submit-error">{error}</div>}

      {/* Backend-authoritative result: rendered from the persisted attempt, not local state. */}
      {completed && attempt && (
        <div className="stack" data-testid="assigned-learning-result">
          <div className="button-row" style={{ alignItems: 'center' }}>
            <span className="status-pill status-pill--approved">Submitted</span>
            {scoreText && <span className="status-pill status-pill--draft" data-testid="assigned-learning-score">Score {scoreText}</span>}
            {attempt.masteryImpact != null && <span className="status-pill status-pill--muted">{attempt.masteryImpact}% mastery</span>}
          </div>
          {attempt.feedbackSummary && <p className="text-muted" data-testid="assigned-learning-feedback">{attempt.feedbackSummary}</p>}
          {Object.keys(attempt.competencyImpact || {}).length > 0 && (
            <div className="stack-sm" data-testid="assigned-learning-competency">
              <strong>Competency impact</strong>
              <div className="admin-chip-list">
                {Object.entries(attempt.competencyImpact).map(([key, value]) => (
                  <span key={key} className="admin-chip">{key.replace(/_/g, ' ')}: {asString(value)}</span>
                ))}
              </div>
            </div>
          )}
          {lesson.nextRecommendedActivity && (
            <p className="helper" data-testid="assigned-learning-next"><strong>Next:</strong> {lesson.nextRecommendedActivity}</p>
          )}
          <div className="stack-sm" data-testid="assigned-learning-content-feedback">
            <strong>Rate this content</strong>
            <div className="button-row">
              <select value={rating} onChange={(event) => setRating(event.target.value)} disabled={feedbackSaved || feedbackSubmitting}>
                <option value="">Rating</option>
                {[5, 4, 3, 2, 1].map((value) => <option key={value} value={value}>{value}/5</option>)}
              </select>
              <select value={reportReason} onChange={(event) => setReportReason(event.target.value)} disabled={feedbackSaved || feedbackSubmitting}>
                <option value="">Report issue</option>
                {['too_easy', 'too_hard', 'confusing', 'incorrect_answer', 'bad_translation', 'irrelevant', 'other'].map((reason) => (
                  <option key={reason} value={reason}>{reason.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            {reportReason && (
              <textarea
                className="input"
                rows={3}
                value={reportDetails}
                disabled={feedbackSaved || feedbackSubmitting}
                onChange={(event) => setReportDetails(event.target.value)}
                placeholder="What should a coach review?"
              />
            )}
            <div className="button-row">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={feedbackSaved || feedbackSubmitting || (!rating && !reportReason)}
                onClick={() => void submitFeedback()}
              >
                {feedbackSaved ? 'Feedback saved' : feedbackSubmitting ? 'Saving...' : 'Send feedback'}
              </button>
            </div>
          </div>
        </div>
      )}

      {!completed && (
        <div className="button-row">
          <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => void submit()} data-testid="assigned-learning-submit">
            {submitting ? 'Submitting…' : 'Submit answers'}
          </button>
        </div>
      )}
    </div>
  )
}

interface AssignedLearningProps {
  // Called after an attempt is submitted so the parent can refresh recommendations and
  // recent activity that the attempt feeds.
  onAttemptSubmitted?: () => void | Promise<void>
}

// "Assigned Learning" section of the learner home: lessons and quizzes pushed by an AI
// coach or human mentor. A learner only ever sees their own assignments (server-gated).
export const AssignedLearning: React.FC<AssignedLearningProps> = ({ onAttemptSubmitted }) => {
  const [assignments, setAssignments] = useState<TemplateAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await learningService.listAssignments()
      const rows=response.data.assignments||[];setAssignments(rows);
      const requested=new URLSearchParams(window.location.search).get('assignmentId');if(requested&&rows.some(row=>row.id===requested))setOpenId(requested)
    } catch {
      setError('We could not load your assigned learning. Please retry.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const handleCompleted = useCallback(async (assignmentId: string) => {
    setAssignments((prev) => prev.map((item) => item.id === assignmentId
      ? { ...item, status: 'completed', completionStatus: 'completed' }
      : item))
    if (onAttemptSubmitted) await onAttemptSubmitted()
  }, [onAttemptSubmitted])

  const openAssignment = assignments.find((item) => item.id === openId) || null

  return (
    <div className="stack" data-testid="assigned-learning">
      <h3>Assigned Learning</h3>
      <p className="text-muted">Lessons and quizzes from your coaches and mentors. Complete them to build your skills and certifications.</p>

      {loading && <p className="text-muted" data-testid="assigned-learning-loading">Loading your assignments…</p>}
      {error && (
        <div className="stack-sm">
          <div className="admin-error-banner" role="alert" data-testid="assigned-learning-error">{error}</div>
          <div className="button-row"><button type="button" className="btn btn-secondary" onClick={() => void load()}>Retry</button></div>
        </div>
      )}

      {!loading && !error && assignments.length === 0 && (
        <p className="text-muted" data-testid="assigned-learning-empty">No assigned lessons yet. Your coach can assign lessons that show up here.</p>
      )}

      {!loading && !error && assignments.length > 0 && (
        <div className="card-grid" data-testid="assigned-learning-list">
          {assignments.map((assignment) => {
            const due = formatDate(assignment.dueDate)
            const difficulty = difficultyLabel(assignment.templateDifficulty)
            return (
              <div key={assignment.id} className="card stack" data-testid="assigned-learning-card">
                <div className="button-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <strong>{assignment.templateTitle || 'Assigned lesson'}</strong>
                  <span className={statusPillClass(assignment.status)}>{statusLabel(assignment)}</span>
                </div>
                <span className="text-muted">{[assignment.templateSkill, difficulty].filter(Boolean).join(' · ') || 'Lesson'}</span>
                <div className="button-row" style={{ flexWrap: 'wrap' }}>
                  <span className="status-pill status-pill--muted">By {assignedByLabel(assignment)}</span>
                  {due && <span className="status-pill status-pill--muted" data-testid="assigned-learning-due">Due {due}</span>}
                </div>
                <div className="button-row">
                  <button type="button" className="btn btn-primary" data-testid="assigned-learning-open" onClick={() => setOpenId(assignment.id)}>
                    {assignment.status === 'completed' ? 'Review' : 'Open'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {openAssignment && (
        <AssignmentRunner
          key={openAssignment.id}
          assignment={openAssignment}
          onClose={() => setOpenId(null)}
          onCompleted={handleCompleted}
        />
      )}
    </div>
  )
}
